import { pageMetaTitle } from "@/lib/seo";
import { createFileRoute, Link, notFound, Outlet, useLocation } from "@tanstack/react-router";
import { ArrowUpRight, MapPin, ChartNoAxesCombined, Code2, ServerCog } from "lucide-react";
import { getPage } from "@/content/pages";
import { Asterisk } from "@/components/Asterisk";
import { ContactForm } from "@/components/ContactForm";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";
import { PageHero } from "@/components/PageHero";
import { MediaImage } from "@/components/MediaImage";
import ServiceAreasImage from "@/assets/service-areas.jpg";
import areaBarnsley from "@/assets/area-barnsley.jpg";
import areaSheffield from "@/assets/area-sheffield.jpg";
import areaLeeds from "@/assets/area-leeds.jpg";
import areaManchester from "@/assets/area-manchester.jpg";
import areaDoncaster from "@/assets/area-doncaster.jpg";
import areaYork from "@/assets/area-york.jpg";

const PAGE_KEY = "/areas";

type FeaturedArea = {
  name: string;
  eyebrow: string;
  headline: string;
  image: string;
  alt: string;
  paragraphs: string[];
  highlights: string[];
  to: string;
  cta: string;
};

const featuredAreas: FeaturedArea[] = [
  {
    name: "Barnsley",
    eyebrow: "South Yorkshire · Home base",
    headline: "SEO & web design in Barnsley",
    image: areaBarnsley,
    alt: "Aerial view of Barnsley town centre at golden hour with red brick Victorian buildings",
    paragraphs: [
      "Barnsley is where I'm based, so I understand the local market from the inside — from the retail and hospitality scene around the Glass Works to the industrial estates at Wombwell, Dodworth, and Cortonwood.",
      "I help local trades, ecommerce brands, and service businesses show up for Barnsley search terms, tighten their Google Business Profile, and turn website visits into calls, enquiries, and bookings.",
    ],
    highlights: [
      "Local SEO for Barnsley postcodes S70–S75",
      "Google Business Profile optimisation",
      "Fast, conversion-focused websites",
    ],
    to: "/areas/barnsley",
    cta: "View Barnsley services",
  },
  {
    name: "Sheffield",
    eyebrow: "South Yorkshire",
    headline: "SEO strategy for Sheffield businesses",
    image: areaSheffield,
    alt: "Sheffield city skyline at dusk with modern buildings and the Peak District silhouetted",
    paragraphs: [
      "Sheffield is a competitive market with strong sectors in manufacturing, healthcare, tech, and professional services. Winning search visibility here means going deeper than a generic city keyword list.",
      "I build long-term SEO strategies for Sheffield brands — technical audits, targeted content, and local citations that help you compete against city-centre agencies without the city-centre price tag.",
    ],
    highlights: [
      "Technical SEO audits and roadmaps",
      "Content built around real search demand",
      "Reporting that ties SEO to enquiries",
    ],
    to: "/seo/sheffield",
    cta: "View Sheffield services",
  },
  {
    name: "Leeds",
    eyebrow: "West Yorkshire",
    headline: "Web design & SEO for Leeds",
    image: areaLeeds,
    alt: "Leeds city centre with modern glass office buildings and the River Aire at golden hour",
    paragraphs: [
      "Leeds is Yorkshire's commercial engine, with a saturated web design market and high standards for SEO. Ambitious teams here need websites that load quickly, communicate clearly, and rank consistently.",
      "I design and build conversion-focused sites for Leeds businesses, then support them with ongoing SEO — the kind of steady, measurable growth that outlasts a one-off launch.",
    ],
    highlights: [
      "WordPress and Shopify builds",
      "Core Web Vitals & page speed",
      "SEO-ready information architecture",
    ],
    to: "/seo/leeds",
    cta: "View Leeds services",
  },
  {
    name: "Manchester",
    eyebrow: "Greater Manchester",
    headline: "SEO & hosting support in Manchester",
    image: areaManchester,
    alt: "Manchester skyline with modern skyscrapers and red brick industrial heritage buildings",
    paragraphs: [
      "Manchester businesses come to me for a direct alternative to city-centre agencies — the same technical depth and strategic thinking, delivered by the person doing the work.",
      "I support brands across Greater Manchester with SEO, WordPress and Shopify builds, managed hosting, and ongoing site care. Most projects run remotely with on-site sessions when they add value.",
    ],
    highlights: [
      "SEO for Manchester service industries",
      "Managed hosting with backups & updates",
      "Ongoing website management retainers",
    ],
    to: "/contact",
    cta: "Discuss Manchester services",
  },
  {
    name: "Doncaster",
    eyebrow: "South Yorkshire",
    headline: "Local SEO & web design in Doncaster",
    image: areaDoncaster,
    alt: "Doncaster town centre with the historic Minster tower and market stalls",
    paragraphs: [
      "Doncaster's business community spans logistics, retail, aviation, and a growing digital economy. Local searches convert well here — if your website is set up to catch them.",
      "I help Doncaster firms sharpen their local SEO, restructure outdated websites, and add the schema, speed, and content depth Google now rewards.",
    ],
    highlights: [
      "Local SEO for DN postcodes",
      "Website rebuilds & migrations",
      "Schema markup & rich results",
    ],
    to: "/seo/doncaster",
    cta: "View Doncaster services",
  },
  {
    name: "York",
    eyebrow: "North Yorkshire",
    headline: "SEO & websites for York businesses",
    image: areaYork,
    alt: "York Minster cathedral at golden hour with medieval city walls in the foreground",
    paragraphs: [
      "York's mix of independent retailers, tourism operators, and professional services means SEO here has to work across seasonal search patterns and highly local intent.",
      "I work with York-based teams on visibility, conversion, and ongoing content strategy — sensible, jargon-free SEO that keeps pace with how people actually search today.",
    ],
    highlights: [
      "Seasonal SEO & content planning",
      "Local landing pages that convert",
      "Analytics you can actually read",
    ],
    to: "/contact",
    cta: "Discuss York services",
  },
];

const additionalAreas = [
  { name: "Rotherham", to: "/seo/rotherham", region: "South Yorkshire", blurb: "Local SEO and web support for Rotherham trades, manufacturers, and independent retailers." },
  { name: "Wakefield", to: "/seo/wakefield", region: "West Yorkshire", blurb: "Stronger websites and search strategy for Wakefield brands ready to grow visibility." },
  { name: "Huddersfield", to: "/seo/huddersfield", region: "West Yorkshire", blurb: "Web design and SEO for Huddersfield companies across services, industrial, and B2B." },
  { name: "Bradford", to: "/contact", region: "West Yorkshire", blurb: "Local SEO and website improvements for Bradford businesses ready to compete online." },
  { name: "Hull", to: "/contact", region: "East Yorkshire", blurb: "SEO, web design, and hosting support for Hull companies and the Humber region." },
  { name: "Yorkshire-wide", to: "/contact", region: "Across the county", blurb: "Coverage for South, West, North, and East Yorkshire including smaller towns and villages." },
];

const smallerTowns = [
  "Wombwell", "Worsbrough", "Hoyland", "Penistone", "Dodworth", "Darton",
  "Mexborough", "Conisbrough", "Swinton", "Pontefract", "Castleford", "Ossett",
  "Harrogate", "Ripon", "Knaresborough", "Halifax", "Brighouse", "Elland",
  "Scarborough", "Whitby", "Bridlington",
];

const coreServices = [
  {
    Icon: ChartNoAxesCombined,
    title: "SEO services",
    body: "Technical, content, and local SEO combined into one strategy that lifts visibility and enquiry quality.",
    to: "/seo",
    cta: "View SEO",
  },
  {
    Icon: Code2,
    title: "Web design",
    body: "Conversion-focused websites with responsive layouts, clear messaging, and clean, SEO-ready structure.",
    to: "/web-design",
    cta: "View web design",
  },
  {
    Icon: ServerCog,
    title: "Hosting & care",
    body: "Managed hosting with backups, security, updates, and reliable ongoing support you can actually reach.",
    to: "/hosting",
    cta: "View hosting",
  },
];

export const Route = createFileRoute("/areas")({
  loader: () => {
    const page = getPage(PAGE_KEY);
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData, matches }) => {
    const deepestMatch = matches[matches.length - 1];
    const isAreasHub = deepestMatch?.routeId === "/areas";

    if (!loaderData || !isAreasHub) {
      return { meta: [], links: [] };
    }

    return {
      meta: [
        { title: pageMetaTitle(PAGE_KEY, loaderData.title) },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: pageMetaTitle(PAGE_KEY, loaderData.title) },
        { property: "og:description", content: loaderData.description },
        { property: "og:url", content: "https://www.phil-carr.co.uk/areas" },
      ],
      links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/areas" }],
    };
  },
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Page not found</h1>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: Page,
});

function Page() {
  const location = useLocation();
  const page = Route.useLoaderData();

  if (location.pathname !== "/areas") {
    return <Outlet />;
  }

  return (
    <>
      <PageHero title="Areas I cover across Yorkshire & beyond" backgroundImage={ServiceAreasImage} />

      <article className="inner-content-section container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <div className="row g-5 align-items-center">
          <div className="col-lg-7">
            <p className="demo-section-label">Local coverage · Regional reach</p>
            <h2 className="site-heading-lg mt-4 font-display font-light">
              SEO, web design & hosting across <em>Yorkshire</em> and the North
            </h2>
          </div>
          <div className="col-lg-5">
            <p className="site-copy text-muted-foreground">
              I support businesses across Barnsley, Sheffield, Doncaster, Leeds, Manchester, and the
              wider Yorkshire region — covering South, West, North, and East Yorkshire. Most projects
              run remotely, with on-site sessions where they genuinely add value.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="demo-link-arrow-button group no-underline">
                Discuss your location
                <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
              <Link to="/services" className="demo-link-arrow-button group no-underline">
                Explore services
                <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
          </div>
        </div>

        <div className="area-feature-list mt-16 space-y-14">
          {featuredAreas.map((area, index) => (
            <section key={area.name} className="area-feature-section row g-5 align-items-center">
              <div className={`col-lg-6 ${index % 2 === 1 ? "order-lg-2" : ""}`}>
                <figure className="area-feature-figure demo-image-frame m-0">
                  <MediaImage
                    src={area.image}
                    alt={area.alt}
                    width={1024}
                    height={1024}
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </figure>
              </div>
              <div className="col-lg-6">
                <p className="demo-section-label">{area.eyebrow}</p>
                <h3 className="site-heading-md mt-4 font-display font-light">{area.headline}</h3>
                {area.paragraphs.map((p) => (
                  <p key={p} className="site-copy mt-5 text-muted-foreground">{p}</p>
                ))}
                <ul className="bootstrap-list-row mt-6 row list-none pl-0">
                  {area.highlights.map((h) => (
                    <li key={h} className="col-sm-12 flex items-start gap-3">
                      <Asterisk className="mt-1 h-4 w-4 shrink-0 text-accent" />
                      <span className="text-foreground">{h}</span>
                    </li>
                  ))}
                </ul>
                <Link to={area.to} className="demo-link-arrow-button group mt-8 no-underline">
                  {area.cta}
                  <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </div>
            </section>
          ))}
        </div>
      </article>

      <section className="border-t border-border bg-muted/20">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-20">
          <div className="row g-5 align-items-end">
            <div className="col-lg-7">
              <p className="demo-section-label">More Yorkshire areas</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Also supporting <em>towns and cities</em> across the region
              </h2>
            </div>
            <div className="col-lg-5">
              <p className="site-copy text-muted-foreground">
                Beyond the featured cities above, I regularly work with businesses in these
                Yorkshire hubs — combining local search knowledge with practical web delivery.
              </p>
            </div>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {additionalAreas.map((area) => (
              <Link
                key={area.name}
                to={area.to}
                className="group flex h-full flex-col rounded-[10px] border border-border bg-card p-6 no-underline transition hover:border-accent hover:shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-accent" />
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">
                    {area.region}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-2xl leading-tight text-foreground">
                  {area.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{area.blurb}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:text-accent">
                  View {area.name} services
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-16 rounded-[10px] border border-dashed border-border bg-background p-8 sm:p-10">
            <p className="demo-section-label">Smaller towns & commuter hubs</p>
            <h3 className="site-heading-md mt-4 font-display font-light">
              I also support <em>the wider Yorkshire region</em>
            </h3>
            <p className="mt-4 max-w-3xl text-muted-foreground">
              If your business sits in one of these towns — or somewhere nearby — I can almost
              certainly help. Remote-first delivery means location rarely gets in the way.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2 pl-0">
              {smallerTowns.map((town) => (
                <li
                  key={town}
                  className="inline-flex items-center rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground"
                >
                  {town}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="inner-content-section container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <div className="row g-5 align-items-end">
          <div className="col-lg-7">
            <p className="demo-section-label">Three core services in every area</p>
            <h2 className="site-heading-lg mt-4 font-display font-light">
              The same <em>focused offer</em>, wherever you're based
            </h2>
          </div>
          <div className="col-lg-5">
            <p className="site-copy text-muted-foreground">
              Every project draws on the same three core capabilities — combined in whatever mix
              your business, sector, and stage of growth actually need.
            </p>
          </div>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {coreServices.map(({ Icon, title, body, to, cta }) => (
            <article
              key={title}
              className="flex h-full flex-col rounded-[10px] border border-border bg-card p-8"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-2xl leading-tight">{title}</h3>
              <p className="mt-4 flex-1 text-muted-foreground">{body}</p>
              <Link to={to} className="demo-link-arrow-button group mt-8 no-underline">
                {cta}
                <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <RelatedNews
        slugs={[
          "seo-in-2026-how-local-yorkshire-businesses-can-win-more-customers-from-google",
          "local-seo-and-the-consumer-journey",
          "local-search-in-2025-personalisation-and-contextual-relevance",
        ]}
      />

      <CtaBand />

      <section className="inner-contact-section border-t border-border bg-background/95">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-start">
            <div className="col-lg-5">
              <p className="demo-section-label">Not sure if I cover your area?</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Let's <em>check together</em>
              </h2>
              <p className="site-copy mt-6 text-muted-foreground">
                If your town is anywhere in Yorkshire or the wider North of England, I can almost
                always help. Send a quick message and I'll confirm coverage and next steps.
              </p>
            </div>
            <div className="col-lg-7">
              <ContactForm pageContext={page.title} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
